/**
 * Form state and submit handler for the free SEO audit (English and Bengali
 * pages). Validates the URL in the browser, then calls the server function.
 * Each page passes its own wording for the three failure cases.
 */
import { useState, type FormEvent } from "react";
import { useServerFn } from "@tanstack/react-start";
import { auditSite } from "@/lib/site-audit.functions";
import { normalizeAuditUrl, type AuditResult } from "@/lib/site-audit";

export type AuditMessages = {
  /** Shown when the URL fails browser-side validation. */
  invalid: (validationError: string) => string;
  /** Shown when the server could not audit the page. */
  serverError: (serverError: string) => string;
  /** Shown when the request itself failed. */
  failed: string;
};

export function useSiteAudit(messages: AuditMessages) {
  const runAudit = useServerFn(auditSite);
  const [url, setUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<AuditResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setResult(null);
    const check = normalizeAuditUrl(url);
    if ("error" in check) {
      setError(messages.invalid(check.error));
      return;
    }
    setLoading(true);
    try {
      const res = await runAudit({ data: { url: check.url.toString() } });
      if ("error" in res) setError(messages.serverError(res.error));
      else setResult(res);
    } catch {
      setError(messages.failed);
    } finally {
      setLoading(false);
    }
  };

  return { url, setUrl, loading, result, error, onSubmit };
}
